import { useEffect, useState, useCallback } from "react";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const inp = "w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500";
const lbl = "text-xs font-medium text-slate-500 mb-1 block";

export const titleize = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());

function Field({ label, hint, children }) {
  return (
    <div>
      <label className={lbl}>{label}</label>
      {children}
      {hint && <p className="text-[11px] text-slate-400 mt-0.5">{hint}</p>}
    </div>
  );
}

function Select({ value, onChange, options, placeholder, testid }) {
  return (
    <select className={inp} data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value || null)}>
      <option value="">{placeholder || "— none —"}</option>
      {options.map((o) => <option key={o} value={o}>{titleize(o)}</option>)}
    </select>
  );
}

// ============================================================ Domain Manager
export function DomainManager({ onClose, onChanged }) {
  const [rows, setRows] = useState(null);
  const [edit, setEdit] = useState(null); // domain being edited or {new:true}
  const load = useCallback(() => api.get("/knowledge/domains").then((r) => setRows(r.data.domains || [])).catch(() => setRows([])), []);
  useEffect(() => { load(); }, [load]);

  const save = async (d) => {
    try {
      if (d.id) await api.put(`/knowledge/domains/${d.id}`, d);
      else await api.post("/knowledge/domains", d);
      toast.success("Domain saved"); setEdit(null); load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save domain"); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex justify-end bg-black/30" onClick={onClose} data-testid="kc-domain-manager">
      <div className="w-full max-w-lg bg-slate-50 h-full overflow-y-auto tf-scroll" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2"><Icons.FolderTree className="h-5 w-5 text-indigo-600" /><h2 className="font-heading text-lg font-semibold">Knowledge Domains</h2></div>
          <div className="flex items-center gap-1">
            <button onClick={() => setEdit({ new: true, name: "", description: "", sort_order: 100, status: "active", agent_access: [] })} data-testid="kc-domain-new" className="inline-flex items-center gap-1.5 bg-[#171B2E] text-white text-sm rounded-lg px-3 py-2"><Icons.Plus className="h-4 w-4 text-[#F5A623]" />New</button>
            <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded text-slate-500"><Icons.X className="h-4 w-4" /></button>
          </div>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-xs text-slate-500">Domains are the top-level parent categories that separate fundamentally different kinds of knowledge (tax law vs firm procedure vs client guidance). Assign each collection to a domain to keep retrieval focused.</p>
          {edit && <DomainForm d={edit} onCancel={() => setEdit(null)} onSave={save} />}
          {rows === null ? <Icons.Loader2 className="h-5 w-5 animate-spin text-indigo-500" /> :
            rows.map((d) => (
              <div key={d.id} className="bg-white rounded-xl border border-slate-200 p-4 flex items-start justify-between gap-3" data-testid={`kc-domain-${d.id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-slate-800">{d.name}</span>
                    {d.status === "archived" && <span className="text-[10px] bg-slate-100 text-slate-500 rounded px-1.5 py-0.5">Archived</span>}
                    <span className="text-[11px] text-slate-400">{d.collection_count ?? 0} collections</span>
                  </div>
                  {d.description && <p className="text-xs text-slate-500 mt-1">{d.description}</p>}
                </div>
                <button onClick={() => setEdit(d)} data-testid={`kc-domain-edit-${d.id}`} className="p-1.5 hover:bg-slate-100 rounded text-slate-500 shrink-0"><Icons.Pencil className="h-3.5 w-3.5" /></button>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}

function DomainForm({ d, onCancel, onSave }) {
  const [f, setF] = useState(d);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  return (
    <div className="bg-white rounded-xl border border-indigo-200 p-4 space-y-2.5" data-testid="kc-domain-form">
      <Field label="Name"><input className={inp} data-testid="kc-domain-name" value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. U.S. Tax Authority" /></Field>
      <Field label="Description"><input className={inp} value={f.description || ""} onChange={(e) => set("description", e.target.value)} /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Sort order"><input type="number" className={inp} value={f.sort_order} onChange={(e) => set("sort_order", Number(e.target.value) || 100)} /></Field>
        <Field label="Status"><Select value={f.status} onChange={(v) => set("status", v || "active")} options={["active", "archived"]} placeholder="active" /></Field>
      </div>
      <div className="flex gap-2 pt-1">
        <button onClick={() => f.name.trim() && onSave(f)} disabled={!f.name.trim()} data-testid="kc-domain-save" className="bg-[#1f6feb] text-white rounded-lg px-4 py-2 text-sm font-medium disabled:opacity-50">Save Domain</button>
        <button onClick={onCancel} className="text-sm text-slate-500 px-3">Cancel</button>
      </div>
    </div>
  );
}

// ============================================================ Document Editor
export function DocumentEditor({ itemId, collection, agents, taxonomy, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  const [reprocessing, setReprocessing] = useState(false);
  const [confirmRe, setConfirmRe] = useState(false);
  const [openSec, setOpenSec] = useState(null);

  const load = useCallback(() => api.get(`/knowledge/items/${itemId}`).then((r) => { setData(r.data); setF(r.data.item); }).catch(() => toast.error("Could not load document")), [itemId]);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const listVal = (v) => (Array.isArray(v) ? v.join(", ") : (v || ""));
  const splitList = (v) => (typeof v === "string" ? v.split(",").map((t) => t.trim()).filter(Boolean) : (v || []));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        document_type: f.document_type, authority_level: f.authority_level, jurisdiction: f.jurisdiction,
        status: f.status, version: f.version, source_url: f.source_url, effective_date: f.effective_date,
        superseded_by: f.superseded_by, allow_penny: !!f.allow_penny,
        tax_year: f.tax_year ? Number(f.tax_year) : null,
        applicable_forms: splitList(f.applicable_forms), applicable_services: splitList(f.applicable_services),
        agent_access: Array.isArray(f.agent_access) ? f.agent_access : (collection.agent_access || []),
      };
      await api.patch(`/knowledge/items/${itemId}/metadata`, payload);
      toast.success("Document metadata saved"); onChanged?.(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save metadata"); }
    finally { setSaving(false); }
  };

  const reprocess = async () => {
    setReprocessing(true); setConfirmRe(false);
    try {
      const r = await api.post(`/knowledge/items/${itemId}/reindex`);
      const d = r.data;
      toast.success(`Reprocessed → ${d.section_count} sections${d.fallback ? " (even-sized fallback)" : ""}`);
      load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Reprocess failed"); }
    finally { setReprocessing(false); }
  };

  const toggleAgent = (k) => {
    const cur = Array.isArray(f.agent_access) ? f.agent_access : (collection.agent_access || []);
    set("agent_access", cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k]);
  };
  const grant = f && (Array.isArray(f.agent_access) ? f.agent_access : (collection.agent_access || []));

  return (
    <div className="fixed inset-0 z-[60] flex justify-end bg-black/40" onClick={onClose} data-testid="kc-doc-editor">
      <div className="w-full max-w-2xl bg-slate-50 h-full overflow-y-auto tf-scroll" onClick={(e) => e.stopPropagation()}>
        {!f ? <div className="p-10 text-center"><Icons.Loader2 className="h-6 w-6 animate-spin text-indigo-500 inline" /></div> : (
          <>
            <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-4 flex items-start justify-between z-10">
              <div className="min-w-0">
                <h2 className="font-heading text-lg font-semibold text-slate-900 truncate">{f.title}</h2>
                <p className="text-xs text-slate-500 mt-0.5">{titleize(f.source_type)} · {f.char_count?.toLocaleString() || 0} chars · {data.sections?.length || 0} sections
                  {f.section_fallback && <span className="text-amber-600"> · even-sized fallback</span>}</p>
              </div>
              <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded text-slate-500"><Icons.X className="h-4 w-4" /></button>
            </div>

            <div className="p-5 space-y-4">
              <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3" data-testid="kc-doc-metadata">
                <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Governed Metadata</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Field label="Document type" hint={`Collection default: ${titleize(collection.default_document_type) || "—"}`}>
                    <Select value={f.document_type} onChange={(v) => set("document_type", v)} options={taxonomy.document_types} testid="kc-doc-type" />
                  </Field>
                  <Field label="Authority level" hint={`Collection default: ${titleize(collection.default_authority_level) || "—"}`}>
                    <Select value={f.authority_level} onChange={(v) => set("authority_level", v)} options={taxonomy.authority_levels} testid="kc-doc-authority" />
                  </Field>
                  <Field label="Jurisdiction" hint={`Collection default: ${titleize(collection.default_jurisdiction) || "—"}`}>
                    <Select value={f.jurisdiction} onChange={(v) => set("jurisdiction", v)} options={taxonomy.jurisdictions} testid="kc-doc-jurisdiction" />
                  </Field>
                  <Field label="Status"><Select value={f.status} onChange={(v) => set("status", v)} options={taxonomy.doc_statuses} testid="kc-doc-status" /></Field>
                  <Field label="Tax year"><input type="number" className={inp} data-testid="kc-doc-taxyear" value={f.tax_year || ""} onChange={(e) => set("tax_year", e.target.value)} placeholder="e.g. 2025" /></Field>
                  <Field label="Version"><input className={inp} data-testid="kc-doc-version" value={f.version || ""} onChange={(e) => set("version", e.target.value)} /></Field>
                  <Field label="Effective date"><input type="date" className={inp} data-testid="kc-doc-effective" value={(f.effective_date || "").slice(0, 10)} onChange={(e) => set("effective_date", e.target.value || null)} /></Field>
                  <Field label="Superseded by (document id)"><input className={inp} value={f.superseded_by || ""} onChange={(e) => set("superseded_by", e.target.value || null)} placeholder="optional" /></Field>
                </div>
                <Field label="Applicable forms (comma-separated)"><input className={inp} data-testid="kc-doc-forms" value={listVal(f.applicable_forms)} onChange={(e) => set("applicable_forms", e.target.value)} placeholder="e.g. 2555, 1116, 8938" /></Field>
                <Field label="Applicable services (comma-separated)"><input className={inp} data-testid="kc-doc-services" value={listVal(f.applicable_services)} onChange={(e) => set("applicable_services", e.target.value)} /></Field>
                <Field label="Source URL / original reference"><input className={inp} data-testid="kc-doc-source" value={f.source_url || f.url || ""} onChange={(e) => set("source_url", e.target.value)} placeholder="https://www.irs.gov/…" /></Field>
              </div>

              <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3" data-testid="kc-doc-access">
                <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide">AI Access</div>
                <div className="flex flex-wrap gap-2">
                  {agents.map((a) => {
                    const on = grant.includes(a.key);
                    return <button key={a.key} type="button" onClick={() => toggleAgent(a.key)} data-testid={`kc-doc-agent-${a.key}`}
                      className={`text-xs px-2.5 py-1.5 rounded-lg border transition-colors ${on ? "bg-violet-600 text-white border-violet-600" : "bg-white text-slate-600 border-slate-300 hover:border-violet-400"}`}>{a.name}</button>;
                  })}
                </div>
                <label className="flex items-center gap-2 text-sm text-slate-700 pt-1" data-testid="kc-doc-allow-penny-wrap">
                  <input type="checkbox" checked={!!f.allow_penny} data-testid="kc-doc-allow-penny" onChange={(e) => set("allow_penny", e.target.checked)} className="accent-violet-600" />
                  Allow Penny access (override) — expose this technical document to the client-facing agent
                </label>
                <p className="text-[11px] text-slate-400">Penny is otherwise restricted to client-facing document types. Enable only for documents approved for client-facing use.</p>
              </div>

              <div className="flex items-center gap-2">
                <button onClick={save} disabled={saving} data-testid="kc-doc-save" className="bg-[#1f6feb] text-white rounded-lg px-4 py-2 text-sm font-medium disabled:opacity-50 inline-flex items-center gap-2">{saving && <Icons.Loader2 className="h-4 w-4 animate-spin" />}Save Metadata</button>
                {!confirmRe ? (
                  <button onClick={() => setConfirmRe(true)} disabled={reprocessing} data-testid="kc-doc-reprocess" className="border border-slate-300 text-slate-700 rounded-lg px-4 py-2 text-sm inline-flex items-center gap-2 disabled:opacity-50">{reprocessing ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : <Icons.RefreshCw className="h-4 w-4" />}Reprocess Document</button>
                ) : (
                  <span className="inline-flex items-center gap-2 text-sm">
                    <span className="text-slate-500">Rebuild sections?</span>
                    <button onClick={reprocess} data-testid="kc-reprocess-confirm" className="bg-amber-500 text-white rounded-lg px-3 py-1.5 text-sm">Confirm</button>
                    <button onClick={() => setConfirmRe(false)} className="text-slate-500 text-sm px-1">Cancel</button>
                  </span>
                )}
              </div>
              <p className="text-[11px] text-slate-400 -mt-2">Reprocessing rebuilds the heading-based sections. The original uploaded file and extracted text are never modified.</p>

              <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="kc-section-preview-panel">
                <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Detected Sections ({data.sections?.length || 0})</div>
                {(!data.sections || data.sections.length === 0) ? <p className="text-sm text-slate-400">No sections. Reprocess to index this document.</p> :
                  <div className="space-y-1.5">
                    {data.sections.map((s) => (
                      <div key={s.id} className="border border-slate-100 rounded-lg" data-testid={`kc-section-${s.order}`}>
                        <button onClick={() => setOpenSec(openSec === s.id ? null : s.id)} className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-slate-50">
                          <span className="text-sm text-slate-700 truncate"><span className="text-slate-400 mr-1.5">#{s.order + 1}</span>{s.heading}</span>
                          <span className="flex items-center gap-2 shrink-0">
                            {s.identifiers?.length > 0 && <span className="text-[10px] bg-blue-50 text-blue-600 rounded px-1.5 py-0.5">{s.identifiers.length} id</span>}
                            <span className="text-[10px] text-slate-400">{s.char_count} ch</span>
                            <Icons.ChevronDown className={`h-3.5 w-3.5 text-slate-400 transition-transform ${openSec === s.id ? "rotate-180" : ""}`} />
                          </span>
                        </button>
                        {openSec === s.id && <div className="px-3 pb-3 text-xs text-slate-600 whitespace-pre-wrap border-t border-slate-100 pt-2">{s.text?.slice(0, 1500)}{(s.text?.length || 0) > 1500 ? "…" : ""}</div>}
                      </div>
                    ))}
                  </div>}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ============================================================ Knowledge Gap Log
const GAP_REASON_LABEL = { zero_results: "No results", low_confidence: "Low confidence", conflicting_sources: "Conflicting sources", insufficient_authority: "Weak authority" };
const GAP_REASON_STYLE = { zero_results: "bg-rose-50 text-rose-600", low_confidence: "bg-amber-50 text-amber-700", conflicting_sources: "bg-orange-50 text-orange-700", insufficient_authority: "bg-violet-50 text-violet-700" };

export function GapLog({ onClose }) {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("unresolved");
  const [agent, setAgent] = useState("");
  const [reason, setReason] = useState("");
  const [q, setQ] = useState("");
  const [threshold, setThreshold] = useState("");
  const [noteFor, setNoteFor] = useState(null);
  const [note, setNote] = useState("");

  const load = useCallback(() => {
    const params = {};
    if (status !== "unresolved") params.status = status;
    if (agent) params.agent = agent;
    if (reason) params.reason = reason;
    if (q) params.q = q;
    api.get("/knowledge/gaps", { params }).then((r) => { setData(r.data); setThreshold(String(r.data.threshold)); }).catch(() => setData({ gaps: [], counts: {} }));
  }, [status, agent, reason, q]);
  useEffect(() => { load(); }, [load]);

  const update = async (id, body) => {
    try { await api.patch(`/knowledge/gaps/${id}`, body); toast.success("Gap updated"); setNoteFor(null); setNote(""); load(); }
    catch { toast.error("Could not update gap"); }
  };
  const saveThreshold = async () => {
    try { await api.put("/knowledge/gaps-config", { threshold: Number(threshold) }); toast.success("Confidence threshold saved"); load(); }
    catch { toast.error("Could not save threshold"); }
  };

  const gaps = data?.gaps || [];
  return (
    <div className="fixed inset-0 z-[60] flex justify-end bg-black/40" onClick={onClose} data-testid="kc-gap-log">
      <div className="w-full max-w-3xl bg-slate-50 h-full overflow-y-auto tf-scroll" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-4 z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2"><Icons.SearchX className="h-5 w-5 text-indigo-600" /><h2 className="font-heading text-lg font-semibold">Knowledge Gaps</h2></div>
            <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded text-slate-500"><Icons.X className="h-4 w-4" /></button>
          </div>
          <p className="text-xs text-slate-500 mt-1">A prioritized backlog of questions the knowledge base answered weakly — grouped by intent, most-asked first. Resolve after adding the missing knowledge.</p>
          <div className="flex flex-wrap items-center gap-2 mt-3">
            {["unresolved", "new", "reviewed", "resolved"].map((s) => (
              <button key={s} onClick={() => setStatus(s)} data-testid={`kc-gap-status-${s}`}
                className={`text-xs px-2.5 py-1.5 rounded-lg border ${status === s ? "bg-[#171B2E] text-white border-[#171B2E]" : "bg-white text-slate-600 border-slate-300"}`}>
                {titleize(s)}{data?.counts && s !== "unresolved" ? ` (${data.counts[s] ?? 0})` : ""}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-2">
            <input className={inp + " max-w-[220px]"} data-testid="kc-gap-search" placeholder="Search queries…" value={q} onChange={(e) => setQ(e.target.value)} />
            <select className={inp + " max-w-[130px]"} data-testid="kc-gap-agent" value={agent} onChange={(e) => setAgent(e.target.value)}><option value="">All agents</option><option value="allison">Allison</option><option value="penny">Penny</option></select>
            <select className={inp + " max-w-[160px]"} data-testid="kc-gap-reason" value={reason} onChange={(e) => setReason(e.target.value)}><option value="">All reasons</option>{Object.keys(GAP_REASON_LABEL).map((r) => <option key={r} value={r}>{GAP_REASON_LABEL[r]}</option>)}</select>
            <div className="flex items-center gap-1 ml-auto text-xs text-slate-500">
              <span title="Retrievals scoring below this are flagged as low-confidence">Confidence threshold</span>
              <input type="number" step="0.1" className={inp + " w-20"} data-testid="kc-gap-threshold" value={threshold} onChange={(e) => setThreshold(e.target.value)} />
              <button onClick={saveThreshold} data-testid="kc-gap-threshold-save" className="text-xs bg-slate-100 hover:bg-slate-200 rounded px-2 py-1.5">Save</button>
            </div>
          </div>
        </div>

        <div className="p-5 space-y-2.5" data-testid="kc-gap-list">
          {data === null ? <Icons.Loader2 className="h-5 w-5 animate-spin text-indigo-500" /> :
            gaps.length === 0 ? <div className="text-center py-16 text-slate-400"><Icons.CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-emerald-400" /><p className="text-sm">No knowledge gaps here.</p></div> :
              gaps.map((g) => (
                <div key={g.id} className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`kc-gap-${g.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-slate-800">{g.sample_query}</div>
                      <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                        <span className="text-[11px] bg-slate-800 text-white rounded-full px-2 py-0.5">×{g.occurrences}</span>
                        {(g.reasons || [g.reason]).map((r) => <span key={r} className={`text-[10px] rounded px-1.5 py-0.5 ${GAP_REASON_STYLE[r] || "bg-slate-100 text-slate-600"}`}>{GAP_REASON_LABEL[r] || r}</span>)}
                        <span className="text-[10px] bg-slate-100 text-slate-500 rounded px-1.5 py-0.5">{titleize(g.agent)}</span>
                        {g.user_role && <span className="text-[10px] text-slate-400">{titleize(g.user_role)}</span>}
                        <span className="text-[10px] text-slate-400">score {g.top_score}</span>
                        {g.escalated && <span className="text-[10px] bg-blue-50 text-blue-600 rounded px-1.5 py-0.5 inline-flex items-center gap-0.5"><Icons.ArrowUpRight className="h-3 w-3" />escalated</span>}
                        {g.status === "reviewed" && <span className="text-[10px] bg-indigo-50 text-indigo-600 rounded px-1.5 py-0.5">reviewed</span>}
                        {g.status === "resolved" && <span className="text-[10px] bg-emerald-50 text-emerald-600 rounded px-1.5 py-0.5">resolved</span>}
                      </div>
                      <div className="text-[11px] text-slate-400 mt-1.5">
                        {g.routed_collections?.length > 0 && <span>Routed: {g.routed_collections.slice(0, 3).join(", ")}{g.routed_collections.length > 3 ? "…" : ""} · </span>}
                        {g.filters?.tax_year && <span>TY {g.filters.tax_year} · </span>}
                        {g.filters?.identifiers?.length > 0 && <span>{g.filters.identifiers.join(", ")} · </span>}
                        last seen {(g.last_seen || "").slice(0, 10)}
                      </div>
                      {g.resolution_note && <div className="text-[11px] text-emerald-700 mt-1 italic">Resolution: {g.resolution_note}</div>}
                    </div>
                    {g.status !== "resolved" && (
                      <div className="flex flex-col gap-1 shrink-0">
                        {g.status !== "reviewed" && <button onClick={() => update(g.id, { status: "reviewed" })} data-testid={`kc-gap-review-${g.id}`} className="text-xs border border-slate-300 text-slate-600 rounded px-2 py-1 hover:bg-slate-50">Mark reviewed</button>}
                        <button onClick={() => { setNoteFor(g.id); setNote(""); }} data-testid={`kc-gap-resolve-${g.id}`} className="text-xs bg-emerald-600 text-white rounded px-2 py-1">Resolve</button>
                      </div>
                    )}
                  </div>
                  {noteFor === g.id && (
                    <div className="mt-2.5 flex items-center gap-2 border-t border-slate-100 pt-2.5">
                      <input className={inp} data-testid={`kc-gap-note-${g.id}`} placeholder="What knowledge did you add? (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
                      <button onClick={() => update(g.id, { status: "resolved", resolution_note: note || null })} data-testid={`kc-gap-resolve-confirm-${g.id}`} className="text-xs bg-emerald-600 text-white rounded px-3 py-2 shrink-0">Confirm resolve</button>
                      <button onClick={() => setNoteFor(null)} className="text-xs text-slate-500 px-1 shrink-0">Cancel</button>
                    </div>
                  )}
                </div>
              ))}
        </div>
      </div>
    </div>
  );
}
export function TestRetrieval({ agents, collections, onClose }) {
  const [agent, setAgent] = useState("allison");
  const [query, setQuery] = useState("");
  const [taxYear, setTaxYear] = useState("");
  const [scope, setScope] = useState([]); // collection ids
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const colName = (id) => collections.find((c) => c.id === id)?.name || id;
  const agentOpts = agents.filter((a) => ["allison", "penny"].includes(a.key));

  const run = async () => {
    if (!query.trim()) return;
    setBusy(true); setRes(null);
    try {
      const r = await api.post("/knowledge/retrieve-test", {
        agent_key: agent, query, tax_year: taxYear ? Number(taxYear) : null,
        collection_ids: scope.length ? scope : null,
      });
      setRes(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Retrieval test failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex justify-end bg-black/40" onClick={onClose} data-testid="kc-test-retrieval">
      <div className="w-full max-w-3xl bg-slate-50 h-full overflow-y-auto tf-scroll" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-2"><Icons.FlaskConical className="h-5 w-5 text-indigo-600" /><h2 className="font-heading text-lg font-semibold">Test Retrieval</h2></div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded text-slate-500"><Icons.X className="h-4 w-4" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
            <Field label="Query"><textarea className={inp} rows={2} data-testid="kc-test-query" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="e.g. Explain the physical presence test for Form 2555" /></Field>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="Agent">
                <select className={inp} data-testid="kc-test-agent" value={agent} onChange={(e) => setAgent(e.target.value)}>
                  {agentOpts.map((a) => <option key={a.key} value={a.key}>{a.name}</option>)}
                </select>
              </Field>
              <Field label="Tax year (optional)"><input type="number" className={inp} data-testid="kc-test-taxyear" value={taxYear} onChange={(e) => setTaxYear(e.target.value)} placeholder="e.g. 2025" /></Field>
              <Field label="Scope (optional)">
                <select className={inp} data-testid="kc-test-scope" value={scope[0] || ""} onChange={(e) => setScope(e.target.value ? [e.target.value] : [])}>
                  <option value="">All collections (routed)</option>
                  {collections.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </Field>
            </div>
            <button onClick={run} disabled={busy || !query.trim()} data-testid="kc-test-run" className="bg-[#1f6feb] text-white rounded-lg px-4 py-2 text-sm font-medium disabled:opacity-50 inline-flex items-center gap-2">{busy && <Icons.Loader2 className="h-4 w-4 animate-spin" />}Run Retrieval</button>
          </div>

          {res && (
            <div className="space-y-3" data-testid="kc-test-results">
              <div className="bg-white rounded-xl border border-slate-200 p-4 text-sm space-y-1.5">
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-slate-600">
                  <span><b>{res.sources.length}</b> results</span>
                  <span>Routing: <b>{res.routing?.confident ? "confident" : "widened (low confidence)"}</b></span>
                  <span>Excluded sections: <b className={res.denied_sections ? "text-rose-500" : ""}>{res.denied_sections}</b></span>
                  {res.engagement_year && <span>Tax year: <b>{res.engagement_year}</b></span>}
                </div>
                {res.query_identifiers?.length > 0 && <div className="flex flex-wrap items-center gap-1.5"><span className="text-[11px] text-slate-400">Detected identifiers:</span>{res.query_identifiers.map((i) => <span key={i} className="text-[11px] bg-blue-50 text-blue-700 rounded px-1.5 py-0.5">{i}</span>)}</div>}
                <div className="flex flex-wrap items-center gap-1.5"><span className="text-[11px] text-slate-400">Routed collections:</span>{(res.routing?.candidate_collections || []).map((id) => <span key={id} className="text-[11px] bg-slate-100 text-slate-600 rounded px-1.5 py-0.5">{colName(id)}</span>)}</div>
                {res.denied_sections > 0 && agent === "penny" && <p className="text-[11px] text-amber-600 inline-flex items-center gap-1"><Icons.ShieldAlert className="h-3.5 w-3.5" />{res.denied_sections} section(s) excluded — likely blocked by Penny's client-facing restriction, permissions, status, or superseded flag.</p>}
                {res.metadata_retrieval?.active && (
                  <div className="flex flex-wrap items-center gap-1.5 pt-1 border-t border-slate-100 mt-1" data-testid="kc-test-metadata-retrieval">
                    <span className="text-[11px] text-indigo-500 inline-flex items-center gap-1 font-medium"><Icons.Sparkles className="h-3 w-3" />AI metadata</span>
                    {res.metadata_retrieval.fallback
                      ? <span className="text-[11px] bg-slate-100 text-slate-500 rounded px-1.5 py-0.5">fallback · {res.metadata_retrieval.fallback_reason || "no matches"}</span>
                      : <>
                          {res.metadata_retrieval.boosted_sections > 0 && <span className="text-[11px] bg-indigo-50 text-indigo-700 rounded px-1.5 py-0.5">boosted {res.metadata_retrieval.boosted_sections}</span>}
                          {res.metadata_retrieval.applied_filter && res.metadata_retrieval.excluded_items > 0 && <span className="text-[11px] bg-amber-50 text-amber-700 rounded px-1.5 py-0.5">excluded {res.metadata_retrieval.excluded_items} off-jurisdiction</span>}
                        </>}
                    {(res.metadata_retrieval.dimensions?.target_countries || []).map((c) => <span key={c} className="text-[11px] bg-blue-50 text-blue-700 rounded px-1.5 py-0.5">{c}</span>)}
                  </div>
                )}
              </div>

              {(res.warnings || []).map((w, i) => (
                <div key={i} className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 flex items-start gap-2" data-testid={`kc-test-warning-${w.type}`}>
                  <Icons.AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" /><span>{w.message}</span>
                </div>
              ))}

              {res.sources.length === 0 ? <p className="text-sm text-slate-400 text-center py-6">No results. {agent === "penny" ? "Penny may be restricted from the matching technical documents." : "Try different wording or add Retrieval Keywords."}</p> :
                res.diagnostics.map((d) => {
                  const c = d.components;
                  return (
                    <div key={d.rank} className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`kc-test-result-${d.rank}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-slate-800">#{d.rank} · {d.citation}</div>
                          <div className="text-[11px] text-slate-400 mt-0.5">{d.domain} · {d.collection}</div>
                        </div>
                        <span className="text-sm font-semibold text-indigo-600 shrink-0">{d.score.toFixed(2)}</span>
                      </div>
                      <div className="flex flex-wrap gap-1.5 mt-2.5 text-[11px]">
                        <span className={`rounded px-1.5 py-0.5 ${c.exact_identifier > 0 ? "bg-blue-100 text-blue-700 font-medium" : "bg-slate-100 text-slate-400"}`}>exact:{c.exact_identifier}</span>
                        <span className="rounded px-1.5 py-0.5 bg-slate-100 text-slate-600">lexical:{c.lexical}</span>
                        <span className="rounded px-1.5 py-0.5 bg-slate-100 text-slate-600">keyword:{c.keyword_match}</span>
                        <span className="rounded px-1.5 py-0.5 bg-slate-100 text-slate-600">authority:{titleize(c.authority_level)} ({c.authority_weight})</span>
                        <span className="rounded px-1.5 py-0.5 bg-slate-100 text-slate-600">tax-yr:{c.tax_year_match}</span>
                        <span className="rounded px-1.5 py-0.5 bg-slate-100 text-slate-600">priority:{c.collection_priority}</span>
                        {c.superseded_penalty > 0 && <span className="rounded px-1.5 py-0.5 bg-rose-100 text-rose-700">superseded</span>}
                        <span className="rounded px-1.5 py-0.5 bg-emerald-50 text-emerald-700">access: {d.access.reason}</span>
                        {(d.metadata_match || []).length > 0 && (d.metadata_match).map((m) => (
                          <span key={m} className="rounded px-1.5 py-0.5 bg-indigo-50 text-indigo-700 font-medium" title="Why AI metadata matched this source (not a statement about legal authority)">match:{m}</span>
                        ))}
                        {d.metadata_boost > 0 && <span className="rounded px-1.5 py-0.5 bg-indigo-100 text-indigo-700">+{d.metadata_boost.toFixed(2)}</span>}
                      </div>
                    </div>
                  );
                })}

              <details className="bg-white rounded-xl border border-slate-200 p-4" data-testid="kc-test-citation-payload">
                <summary className="text-xs font-semibold text-slate-500 cursor-pointer">Citation payload passed to the AI</summary>
                <pre className="text-[11px] text-slate-600 mt-2 overflow-x-auto whitespace-pre-wrap">{JSON.stringify(res.sources, null, 2)}</pre>
              </details>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
